import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import ImageUpload from "@/components/admin/ImageUpload";
import api, { apiError } from "@/lib/api";

const EMPTY = {
  title_es: "", title_en: "", category_es: "", category_en: "",
  excerpt_es: "", excerpt_en: "", body_es: "", body_en: "", image: "", date: "",
};

export default function AdminBlog() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/blog").then((r) => setPosts(r.data.posts)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setDialogOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title_es: form.title_es, title_en: form.title_en,
      category_es: form.category_es, category_en: form.category_en,
      excerpt_es: form.excerpt_es, excerpt_en: form.excerpt_en,
      body_es: form.body_es, body_en: form.body_en,
      image: form.image, date: form.date,
    };
    try {
      if (editing) await api.put(`/admin/blog/${editing.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(editing ? (es ? "Entrada actualizada" : "Post updated") : (es ? "Entrada creada" : "Post created"));
      setDialogOpen(false); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    await api.delete(`/admin/blog/${deleteId}`);
    toast.success(es ? "Entrada eliminada" : "Post deleted");
    setDeleteId(null); load();
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl font-light">{es ? "Contenido · Blog" : "Content · Blog"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{posts.length} {es ? "entradas" : "posts"}</p>
        </div>
        <Button className="rounded-none" onClick={openNew} data-testid="add-blog-btn"><Plus className="h-4 w-4 mr-1" /> {es ? "Añadir entrada" : "Add post"}</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-4 font-medium overline">{es ? "Título" : "Title"}</th>
                <th className="p-4 font-medium overline hidden md:table-cell">{es ? "Categoría" : "Category"}</th>
                <th className="p-4 font-medium overline hidden sm:table-cell">{es ? "Fecha" : "Date"}</th>
                <th className="p-4 font-medium overline text-right">{es ? "Acciones" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`blog-row-${p.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-10 bg-secondary overflow-hidden shrink-0"><img src={p.image} alt="" className="h-full w-full object-cover" /></div>
                      <p className="font-medium">{p[`title_${lang}`] || p.title_es}</p>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-muted-foreground">{p[`category_${lang}`]}</td>
                  <td className="p-4 hidden sm:table-cell text-muted-foreground">{p.date}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-blog-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(p.id)} className="hover:text-destructive" data-testid={`delete-blog-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? (es ? "Editar entrada" : "Edit post") : (es ? "Nueva entrada" : "New post")}</DialogTitle>
            <DialogDescription className="sr-only">Blog post form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="overline text-muted-foreground">Título (ES)</Label><Input required value={form.title_es} onChange={set("title_es")} className="rounded-none mt-1.5" data-testid="bf-title-es" /></div>
              <div><Label className="overline text-muted-foreground">Title (EN)</Label><Input required value={form.title_en} onChange={set("title_en")} className="rounded-none mt-1.5" data-testid="bf-title-en" /></div>
              <div><Label className="overline text-muted-foreground">Categoría (ES)</Label><Input value={form.category_es} onChange={set("category_es")} className="rounded-none mt-1.5" data-testid="bf-cat-es" /></div>
              <div><Label className="overline text-muted-foreground">Category (EN)</Label><Input value={form.category_en} onChange={set("category_en")} className="rounded-none mt-1.5" data-testid="bf-cat-en" /></div>
            </div>
            <div><Label className="overline text-muted-foreground">Extracto (ES)</Label><Textarea rows={2} value={form.excerpt_es} onChange={set("excerpt_es")} className="rounded-none mt-1.5 resize-none" data-testid="bf-excerpt-es" /></div>
            <div><Label className="overline text-muted-foreground">Excerpt (EN)</Label><Textarea rows={2} value={form.excerpt_en} onChange={set("excerpt_en")} className="rounded-none mt-1.5 resize-none" data-testid="bf-excerpt-en" /></div>
            <div><Label className="overline text-muted-foreground">Contenido (ES) — {es ? "separa párrafos con una línea en blanco" : "separate paragraphs with a blank line"}</Label><Textarea rows={5} value={form.body_es} onChange={set("body_es")} className="rounded-none mt-1.5 resize-none" data-testid="bf-body-es" /></div>
            <div><Label className="overline text-muted-foreground">Content (EN)</Label><Textarea rows={5} value={form.body_en} onChange={set("body_en")} className="rounded-none mt-1.5 resize-none" data-testid="bf-body-en" /></div>
            <div className="grid sm:grid-cols-2 gap-4 items-end">
              <div><Label className="overline text-muted-foreground">{es ? "Fecha (texto)" : "Date (text)"}</Label><Input value={form.date} onChange={set("date")} placeholder="15 May 2026" className="rounded-none mt-1.5" data-testid="bf-date" /></div>
            </div>
            <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label={es ? "Imagen destacada" : "Featured image"} testid="bf-image" />
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="rounded-none flex-1" disabled={saving} data-testid="save-blog-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? (es ? "Guardar cambios" : "Save changes") : (es ? "Crear entrada" : "Create post"))}</Button>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>{es ? "Cancelar" : "Cancel"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader><AlertDialogTitle>{es ? "¿Eliminar esta entrada?" : "Delete this post?"}</AlertDialogTitle><AlertDialogDescription>{es ? "Esta acción no se puede deshacer." : "This action cannot be undone."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{es ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-blog">{es ? "Eliminar" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
